import api from './api';
import { MONTH_BASELINE_PRINT, parseFeatures } from '../utils/planPricing';

export const subscriberApi = {
  getAll: (params = {}) => api.get('/subscribers', { params }),
  getOne: (id) => api.get(`/subscribers/${id}`),
  create: (payload) => api.post('/subscribers', payload),
  update: (id, payload) => api.put(`/subscribers/${id}`, payload),
  remove: (id) => api.delete(`/subscribers/${id}`),

  addPayment: (id, payload) => api.post(`/subscribers/${id}/add-payment`, payload),
  renew: (id, payload) => api.post(`/subscribers/${id}/renew`),
  deactivate: (id) => api.post(`/subscribers/${id}/deactivate`),
  activate: (id) => api.post(`/subscribers/${id}/activate`),
  exportExcel: (params = {}) => api.get('/subscribers/export/excel', { params, responseType: 'blob' }),
};

const PLANS_STORAGE_KEY = 'flare_subscription_plans';
const PRINTED_COVER_PRICE_PKR = MONTH_BASELINE_PRINT;

function buildDefaultPlans() {
  const perMonthCover = PRINTED_COVER_PRICE_PKR;
  return [
    {
      id: 'plan-monthly-printed',
      name: 'Monthly Printed',
      duration_months: 1,
      durationMonths: 1,
      price_pkr: perMonthCover,
      description: 'Pay as you go. 1 printed magazine delivered to your door.',
      is_active: true,
      status: 'active',
      includes_physical: true,
      includes_digital: false,
      features: [
        '1 printed issue per month',
        'Nationwide courier delivery',
        'Premium matte laminated copy',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'plan-quarterly-printed',
      name: 'Quarterly Printed',
      duration_months: 3,
      durationMonths: 3,
      price_pkr: Math.round(perMonthCover * 3 * 0.85),
      description: '3 months of printed Flare delivered. Save 15%.',
      is_active: true,
      status: 'active',
      includes_physical: true,
      includes_digital: false,
      features: [
        '3 printed issues (one per month)',
        'Free nationwide delivery',
        'Save 15% vs monthly',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'plan-halfyearly-printed',
      name: 'Half-Yearly Printed',
      duration_months: 6,
      durationMonths: 6,
      price_pkr: Math.round(perMonthCover * 6 * 0.75),
      description: '6 months of printed Flare. Save 25%.',
      is_active: true,
      status: 'active',
      includes_physical: true,
      includes_digital: false,
      features: [
        '6 printed issues delivered monthly',
        'Free nationwide delivery',
        'Save 25% vs monthly',
        'Subscribers-only content access',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'plan-yearly-printed',
      name: 'Yearly Printed · Most Popular',
      duration_months: 12,
      durationMonths: 12,
      price_pkr: Math.round(perMonthCover * 12 * (2 / 3)),
      description: 'Full year of printed Flare. Save 33% — avg Rs 1,000 / month.',
      is_active: true,
      status: 'active',
      includes_physical: true,
      includes_digital: false,
      features: [
        '12 printed issues — delivered each month',
        'Free nationwide courier on every issue',
        'Save 33% vs monthly — avg Rs 1,000 / month',
        'Free digital PDF copies included',
        'Early access to new releases & interviews',
        'Subscribers-only events & giveaways',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    {
      id: 'plan-biennial-printed',
      name: 'Biennial Printed · MAX SAVINGS',
      duration_months: 24,
      durationMonths: 24,
      price_pkr: Math.round(perMonthCover * 24 * 0.6),
      description: '2 full years of printed Flare. Lowest per-issue price — save 40%, avg Rs 900 / month.',
      is_active: true,
      status: 'active',
      includes_physical: true,
      includes_digital: false,
      features: [
        '24 printed issues over 2 years — monthly delivery',
        'Free nationwide courier on every issue',
        'Save 40% vs monthly — avg Rs 900 / month',
        'Free digital PDF copies for every issue',
        'Early access to new releases & interviews',
        'Subscribers-only events & giveaways',
        'Loyalty badge + free annual Flare merch box',
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
  ];
}

function normPlan(p) {
  if (!p || typeof p !== 'object') return null;
  const dm = Number(p.duration_months || p.durationMonths || 1);
  const id = p.id || ('plan-' + Math.random().toString(36).slice(2, 10));
  const price = Number(p.price_pkr ?? p.pricePKR ?? p.price ?? 0);
  const active = p.is_active === undefined ? p.status === undefined ? true : String(p.status).toLowerCase() !== 'inactive' : Boolean(p.is_active);
  let features = parseFeatures(p.features);
  if (!features.length && typeof p.description === 'string' && p.description.length) {
    features = p.description.split(/\n|•/).map(s => s.trim()).filter(Boolean);
  }
  if (!features.length) features = ['Printed magazine copy', 'Doorstep delivery each month', 'Exclusive subscriber discounts'];
  const plan = {
    ...p,
    id,
    name: String(p.name || `Plan ${id}`),
    duration_months: dm,
    durationMonths: dm,
    price_pkr: price,
    pricePKR: price,
    is_active: active,
    status: active ? 'active' : 'inactive',
    includes_physical: p.includes_physical === undefined ? true : Boolean(p.includes_physical),
    includes_digital: Boolean(p.includes_digital),
    features,
    created_at: p.created_at || new Date().toISOString(),
    updated_at: p.updated_at || new Date().toISOString(),
  };
  return plan;
}

const DEFAULT_SEED_IDS = new Set([
  'plan-monthly-printed',
  'plan-quarterly-printed',
  'plan-halfyearly-printed',
  'plan-yearly-printed',
  'plan-biennial-printed',
]);

function dedupePlans(list) {
  if (!Array.isArray(list)) return [];
  const buckets = new Map();
  list.forEach(p => {
    const plan = normPlan(p);
    if (!plan) return;
    const dm = Number(plan.duration_months) || 0;
    if (!buckets.has(dm)) buckets.set(dm, []);
    buckets.get(dm).push(plan);
  });
  const out = [];
  for (const [, bucket] of buckets) {
    if (bucket.length === 1) {
      out.push(bucket[0]);
      continue;
    }
    let best = null;
    bucket.forEach(candidate => {
      if (!best) { best = candidate; return; }
      const bestIsSeed = DEFAULT_SEED_IDS.has(String(best.id));
      const candIsSeed = DEFAULT_SEED_IDS.has(String(candidate.id));
      if (bestIsSeed && !candIsSeed) { best = candidate; return; }
      if (candIsSeed && !bestIsSeed) return;
      const bestIsNumeric = /^\d+$/.test(String(best.id));
      const candIsNumeric = /^\d+$/.test(String(candidate.id));
      if (candIsNumeric && !bestIsNumeric) { best = candidate; return; }
      if (bestIsNumeric && !candIsNumeric) return;
      const bestPrice = Number(best.price_pkr) || 0;
      const candPrice = Number(candidate.price_pkr) || 0;
      if (candPrice < bestPrice) best = candidate;
    });
    if (best) out.push(best);
  }
  return out.sort((a, b) => (Number(a.duration_months) || 0) - (Number(b.duration_months) || 0));
}

function readLocalPlans() {
  try {
    const raw = window.localStorage.getItem(PLANS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return dedupePlans(parsed.map(normPlan).filter(Boolean));
  } catch {
    return null;
  }
}
function writeLocalPlans(list) {
  try { window.localStorage.setItem(PLANS_STORAGE_KEY, JSON.stringify(dedupePlans(list))); } catch {}
}

function toApiPayload(plan) {
  const features = parseFeatures(plan.featuresText != null ? plan.featuresText : plan.features);
  const active = plan.is_active === undefined
    ? String(plan.status || 'active').toLowerCase() !== 'inactive'
    : Boolean(plan.is_active);
  const payload = {
    name: plan.name,
    duration_months: Number(plan.duration_months || plan.durationMonths || 1),
    price_pkr: Number(plan.price_pkr || 0),
    description: plan.description || '',
    features,
    includes_physical: plan.includes_physical !== false,
    status: active ? 'active' : 'inactive',
  };
  const slug = String(plan.slug || '').trim();
  if (slug) payload.slug = slug;
  return payload;
}
function getInitialPlans() {
  let list = readLocalPlans();
  if (!Array.isArray(list) || list.length === 0) {
    list = buildDefaultPlans();
    writeLocalPlans(list);
    return list;
  }
  writeLocalPlans(list);
  return list;
}
export const subscriptionPlanApi = {
  getAll: async (params = {}) => {
    let apiResult = []; let ok = false;
    try {
      const r = await api.get('/subscription-plans', { params });
      const body = r && r.data ? r.data : {};
      const rawList = body?.data || body?.plans || body?.subscription_plans || (Array.isArray(body) ? body : []) || [];
      if (Array.isArray(rawList)) apiResult = rawList;
      ok = true;
    } catch {}
    if (ok && apiResult.length) {
      const merged = dedupePlans(apiResult.map(normPlan).filter(Boolean));
      writeLocalPlans(merged);
      return { data: { data: merged, ok } };
    }
    return { data: { data: getInitialPlans(), ok } };
  },

  getStartIssues: async () => {
    const r = await api.get('/plans/start-issues');
    return r;
  },

  getActive: async () => {
    let apiResult = []; let ok = false;
    try {
      const r = await api.get('/plans');
      const body = r && r.data ? r.data : {};
      const rawList = body?.subscription_plans || body?.plans || body?.data || (Array.isArray(body) ? body : []) || [];
      if (Array.isArray(rawList)) apiResult = rawList;
      ok = true;
    } catch {}
    const fromApi = ok && apiResult.length
      ? dedupePlans(apiResult.map(normPlan).filter(Boolean))
      : getInitialPlans();
    if (ok && apiResult.length) writeLocalPlans(fromApi);
    const merged = fromApi.filter(p => p.is_active !== false && (p.includes_physical === undefined || Boolean(p.includes_physical)));
    return { data: { subscription_plans: merged, plans: merged, ok } };
  },

  checkout: (payload) =>
    api.post('/plans/checkout', payload, payload instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined),

  getOne: async (id) => {
    try {
      const list = getInitialPlans();
      const found = list.find(p => String(p.id) === String(id));
      if (found) return { data: found };
    } catch {}
    return api.get(`/subscription-plans/${id}`);
  },

  create: async (payload) => {
    const apiPayload = toApiPayload(payload);
    const r = await api.post('/subscription-plans', apiPayload);
    const body = r && r.data ? r.data : null;
    const raw = body?.subscription_plan || body?.data || body;
    const saved = normPlan(raw && (raw.id || raw.name) ? raw : apiPayload);
    const list = (readLocalPlans() || []).filter((p) => Number(p.duration_months) !== Number(saved.duration_months));
    list.push(saved);
    writeLocalPlans(list);
    return { data: saved, stored: 'api' };
  },

  update: async (id, payload) => {
    const apiPayload = toApiPayload({ ...payload, id });
    const r = await api.put(`/subscription-plans/${id}`, apiPayload);
    const body = r && r.data ? r.data : null;
    const raw = body?.subscription_plan || body?.data || body;
    const saved = normPlan(raw && (raw.id || raw.name) ? { ...raw, id } : { ...payload, id, ...apiPayload });
    const list = (readLocalPlans() || []).filter((p) => String(p.id) !== String(id));
    list.push(saved);
    writeLocalPlans(list);
    return { data: saved, stored: 'api' };
  },

  remove: async (id) => {
    let stored = 'local';
    try {
      await api.delete(`/subscription-plans/${id}`);
      stored = 'api+local';
    } catch {}
    const list = getInitialPlans().filter(p => String(p.id) !== String(id));
    writeLocalPlans(list);
    return { data: { ok: true, stored } };
  },
};

export const subscriptionApi = {
  getAll: (params = {}) => api.get('/subscriptions', { params }),
  getOne: (id) => api.get(`/subscriptions/${id}`),
  create: (payload) => api.post('/subscriptions', payload),
  update: (id, payload) => api.put(`/subscriptions/${id}`, payload),
  remove: (id) => api.delete(`/subscriptions/${id}`),
};

export const paymentApi = {
  getAll: (params = {}) => api.get('/payments', { params }),
  getOne: (id) => api.get(`/payments/${id}`),
  create: (payload) => api.post('/payments', payload),
  update: (id, payload) => api.put(`/payments/${id}`, payload),
  remove: (id) => api.delete(`/payments/${id}`),
  markReceived: (id) => api.post(`/payments/${id}/mark-received`),
};

export const shippingApi = {
  getCities: () => api.get('/shipping/cities'),
};

export const jazzcashApi = {
  /** Card only — JazzCash page redirection (MPAY). Wallet must use chargeWallet. */
  prepareRedirect: (orderNumbers) =>
    api.post('/payments/jazzcash/prepare', { order_numbers: orderNumbers, channel: 'card' }, { skipAuth: true }),
  /** MWallet REST API: mobile + CNIC last 6 (no page redirect). */
  chargeWallet: (payload) =>
    api.post('/payments/jazzcash/mwallet', payload, { skipAuth: true }),
  abandon: (orderNumbers, reason) =>
    api.post('/payments/jazzcash/abandon', { order_numbers: orderNumbers, reason }, { skipAuth: true }),
  status: (txnRef) =>
    api.post('/payments/jazzcash/status', { txn_ref: txnRef }, { skipAuth: true }),
};

/** Auto-submit JazzCash hosted checkout form (Page Redirection v1.1). */
export function submitJazzCashRedirect(jazzcash) {
  const postUrl = String(jazzcash?.post_url || '').trim();
  const fields = jazzcash?.fields;
  if (!postUrl || !fields || typeof fields !== 'object') {
    throw new Error('JazzCash redirect payload missing.');
  }
  if (!/^https:\/\//i.test(postUrl)) {
    throw new Error('JazzCash payment URL is invalid.');
  }
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = postUrl;
  form.acceptCharset = 'UTF-8';
  form.style.display = 'none';
  Object.entries(fields).forEach(([key, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value == null ? '' : String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

export const ordersApi = {
  storeGuestOrder: (payload) =>
    api.post('/orders/store', payload, payload instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : undefined),
  getPublicByNumber: (orderNumber) =>
    api.get(`/orders/by-number/${encodeURIComponent(orderNumber)}`, { skipAuth: true }),
  deskSummary: () => api.get('/orders/desk-summary'),
  paidCustomers: (params = {}) => api.get('/orders/paid-customers', { params }),
  getAll: (params = {}) => api.get('/single-issue-purchases', { params }),
  getOne: (id) => api.get(`/single-issue-purchases/${id}`),
  update: (id, payload) => api.put(`/single-issue-purchases/${id}`, payload),
  remove: (id) => api.delete(`/single-issue-purchases/${id}`),
  markPaid: (id, payload = {}) => api.post(`/orders/${id}/mark-paid`, payload),
  cancel: (id, payload = {}) => api.post(`/orders/${id}/cancel`, payload),
  markDelivered: (id, payload = {}) => api.post(`/orders/${id}/mark-delivered`, payload),
  printSlips: (id) => api.post(`/orders/${id}/print-slips`),
  ship: (id, payload = {}) => api.post(`/orders/${id}/ship`, payload),
  sendDispatchEmail: (id) => api.post(`/orders/${id}/send-dispatch-email`),
  syncTracking: (id) => api.post(`/orders/${id}/sync-tracking`),
  exportExcel: (params = {}) => api.get('/orders/export-excel', { params }),
};

export const deliveryReportApi = {
  getAll: (params = {}) => api.get('/delivery-reports', { params }),
  getOne: (id) => api.get(`/delivery-reports/${id}`),
  create: (payload) => api.post('/delivery-reports', payload),
  update: (id, payload) => api.put(`/delivery-reports/${id}`, payload),
  remove: (id) => api.delete(`/delivery-reports/${id}`),
  generate: (payload) => api.post('/delivery-reports/generate', payload),
  printSlips: (id) => api.get(`/delivery-reports/${id}/print-slips`),
  exportExcel: (params = {}) => api.get('/delivery-reports/export-excel', { params }),
  monthlyReportData: (params = {}) => api.get('/delivery-reports/monthly-report-data', { params }),
};

export const dashboardApi = {
  stats: () => api.get('/dashboard/stats'),
};
